import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid3x3 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';

interface CourtZoneCardProps {
  courtZone: {
    front_back?: Record<string, number>;
    sides?: Record<string, number>;
    zones?: Record<string, number>;
  } | null;
  delay?: number;
}

// Zone layout mirrors backend/b2b_api 3×3 grid (rows: net, transition, back;
// cols: left, middle, right) and the existing BallMap/Court.tsx coordinates so
// the visualisation matches what's shown in the per-session report.
const ZONE_RECTS = [
  { key: 'net_left', x: 10, y: 0, w: 24, h: 30 },
  { key: 'net_middle', x: 34, y: 0, w: 32, h: 30 },
  { key: 'net_right', x: 66, y: 0, w: 24, h: 30 },
  { key: 'transition_left', x: 10, y: 30, w: 24, h: 15 },
  { key: 'transition_middle', x: 34, y: 30, w: 32, h: 15 },
  { key: 'transition_right', x: 66, y: 30, w: 24, h: 15 },
  { key: 'back_left', x: 10, y: 45, w: 24, h: 30 },
  { key: 'back_middle', x: 34, y: 45, w: 32, h: 30 },
  { key: 'back_right', x: 66, y: 45, w: 24, h: 30 },
] as const;

const CourtZoneCard: React.FC<CourtZoneCardProps> = ({
  courtZone,
  delay = 0,
}) => {
  const { t } = useTranslation(['report']);

  const zones = courtZone?.zones ?? {};
  const values = ZONE_RECTS.map((r) =>
    typeof zones[r.key] === 'number' ? zones[r.key] : 0,
  );
  const maxValue = Math.max(...values, 0);
  const hasZones = maxValue > 0;

  // Map zone fraction → fill opacity (light haze for least-used, near-opaque
  // for most-used). Floor at 0.1 so empty-ish zones are still visible.
  const opacityOf = (v: number): number => {
    if (maxValue <= 0) return 0;
    return 0.15 + (v / maxValue) * 0.75;
  };

  return (
    <Card style={{ animation: `fade-in 0.5s ease-out ${delay}ms both` }}>
      <CardHeader className="flex flex-row items-center gap-3 space-y-0">
        <div className="rounded-lg bg-slate-900 p-2">
          <Grid3x3 className="h-5 w-5 text-white" />
        </div>
        <div className="space-y-0.5">
          <CardTitle className="text-lg">
            {t('report:playerReport.zonePreference')}
          </CardTitle>
          <CardDescription className="text-sm">
            {t('report:playerReport.zonePreferenceSubtitle')}
          </CardDescription>
        </div>
      </CardHeader>

      <CardContent>
        {!hasZones ? (
          <p className="text-sm text-slate-500">
            {t('report:playerReport.zonePreferenceNoData')}
          </p>
        ) : (
          <div className="mx-auto w-full max-w-[480px] rounded-lg bg-slate-600 p-5">
            <svg
              viewBox="0 -2 100 79"
              width="100%"
              className="block h-auto w-full"
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label={t('report:playerReport.zonePreference')}
            >
              {/* Court playing surface */}
              <rect
                x="0"
                y="-2"
                width="100"
                height="79"
                fill="rgb(69, 85, 108)"
              />

              {/* Zone heat overlays */}
              {ZONE_RECTS.map((rect) => {
                const value =
                  typeof zones[rect.key] === 'number' ? zones[rect.key] : 0;
                return (
                  <rect
                    key={rect.key}
                    x={rect.x}
                    y={rect.y}
                    width={rect.w}
                    height={rect.h}
                    fill="hsl(222.2, 47.4%, 11.2%)"
                    fillOpacity={opacityOf(value)}
                  />
                );
              })}

              {/* Net (dashed line at y=0) */}
              <line
                x1="10"
                y1="0"
                x2="90"
                y2="0"
                stroke="rgb(255, 255, 255)"
                strokeWidth="1"
                strokeDasharray="2,2"
              />

              {/* Center service line */}
              <line
                x1="50"
                y1="0"
                x2="50"
                y2="55"
                stroke="rgb(255,255,255)"
                strokeWidth="0.8"
              />

              {/* Service line */}
              <line
                x1="10"
                y1="55"
                x2="90"
                y2="55"
                stroke="rgb(255,255,255)"
                strokeWidth="0.8"
              />

              {/* Half-court outline (open at net side) */}
              <path
                d="M 10 0 L 10 75 L 90 75 L 90 0"
                fill="none"
                stroke="rgb(255, 255, 255)"
                strokeWidth="1.6"
              />

              {/* Zone percentage labels */}
              {ZONE_RECTS.map((rect) => {
                const value =
                  typeof zones[rect.key] === 'number' ? zones[rect.key] : 0;
                const cx = rect.x + rect.w / 2;
                const cy = rect.y + rect.h / 2 + 1.6;
                return (
                  <text
                    key={`label-${rect.key}`}
                    x={cx}
                    y={cy}
                    textAnchor="middle"
                    fontSize="5"
                    fontWeight="600"
                    fill="rgb(255,255,255)"
                    stroke="rgb(0,0,0)"
                    strokeWidth="0.3"
                    paintOrder="stroke"
                    style={{
                      fontFamily: 'ui-sans-serif, system-ui, sans-serif',
                    }}
                  >
                    {Math.round(value * 100)}%
                  </text>
                );
              })}

              {/* Net label tucked above the dashed line */}
              <text
                x="50"
                y="-3.3"
                textAnchor="middle"
                fontSize="3.5"
                fill="rgba(255,255,255,0.7)"
                style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif' }}
              >
                {t('report:playerReport.zones.net').toUpperCase()}
              </text>
            </svg>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CourtZoneCard;
